import {EventEmitter, Injectable, InjectionToken, OnDestroy, Output, TemplateRef} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {AbstractSearchModel} from "@fedeltapointofsale/data-client";
import {ReplaySubject, Subject, Subscription} from "rxjs";

export class RegisteredFormField {
    constructor(public field: string, public label: string, public template: TemplateRef<any>) {
    }
}

/**
 * Token Injected when the common-controls need to function in the extended(pill) mode
 * @type {InjectionToken}
 */
export const SEARCH_FORM_EXTENDED_CONTROLS_MODE: InjectionToken<boolean> = new InjectionToken("SEARCH_FORM_EXTENDED_CONTROLS_MODE");

/**
 * Service used to register the search input components using the ExtendedSearchInputDirective.
 * Also provides other functions for the search extended.
 */
@Injectable()
export class SearchFormExtendedService implements OnDestroy {

    private subscriptions: Subscription = new Subscription();

    /**
     * Keeps the list of all form field templates registered
     * @type {Array}
     */
    registeredFormFields: Array<RegisteredFormField> = [];

    /**
     * keeps the list of form fields selected
     * @type {Array}
     * @private
     */
    private _selectedFormFields: Array<string> = [];

    $selectedFormFields: Subject<Array<string>> = new ReplaySubject(1);

    $registeredTemplate: Subject<any> = new ReplaySubject<any>();

    hasExtendedFields: Subject<boolean> = new ReplaySubject(1);

    get selectedFormFields(): Array<string> {
        return this._selectedFormFields;
    }

    set selectedFormFields(fields: Array<string>) {
        this._selectedFormFields = fields == undefined ? [] : Array.from(fields);
        this.checkExtendedFields();
        this.$selectedFormFields.next(this._selectedFormFields);
    }

    /**
     * Event triggered when a control request a form field to be removed
     * @type {EventEmitter}
     */
    @Output() requestFormFieldRemoved: EventEmitter<string> = new EventEmitter();

    /**
     * Event triggered when a control has been removed from the search
     * @type {EventEmitter}
     */
    @Output() formFieldRemoved: EventEmitter<string> = new EventEmitter();

    /**
     * Current extended field. This value is used to pass the key to the control in its creation.
     */
    currentExtendedField: string;

    constructor(public searchModel: AbstractSearchModel, private translate: TranslateService) {
        this.selectFieldsThatAreOnSearchFilter();
        this.subscriptions.add(this.searchModel.filterFieldChange.subscribe(() => {
            this.selectFieldsThatAreOnSearchFilter();
        }));
    }

    /**
     * Register a template using the label as key
     * @param field
     * @param template
     */
    registerTemplate(field: string, template: TemplateRef<any>): void {
        if (this.registeredFormFields.some((registered: RegisteredFormField) => registered.field === field)) {
            return;
        }
        const rff: RegisteredFormField = new RegisteredFormField(field, "", template);
        this.translate.get("wbo." + this.searchModel.getResource() + ".search.field." + field + ".label").subscribe((translation: string) => rff.label = translation);
        this.registeredFormFields.push(rff);
        // make binding to work
        this.registeredFormFields = Array.from(this.registeredFormFields).sort((f1: RegisteredFormField, f2: RegisteredFormField) => f1.label.localeCompare(f2.label));
        this.checkExtendedFields();

        this.$registeredTemplate.next(this.registeredFormFields);
    }

    removeTemplate(field: string): void {
        this.registeredFormFields = this.registeredFormFields.filter((registeredFormField: RegisteredFormField) => registeredFormField.field !== field);
        this.checkExtendedFields();
        this.$registeredTemplate.next(this.registeredFormFields);

        this.requestFormFieldRemoved.emit(field);
    }

    checkExtendedFields() {
        const hasExtended: boolean = this._selectedFormFields.find((selectedField: string) => this.registeredFormFields.find((regField: RegisteredFormField) => regField.field === selectedField) !== undefined) !== undefined;
        this.hasExtendedFields.next(hasExtended);
    }

    /**
     * Request a form field to be removed. It will trigger an event through requestFormFieldRemoved
     * @param field
     */
    requestRemoveFormField(field: string): void {
        this.requestFormFieldRemoved.emit(field);
    }

    /**
     * Triggers a event that the form has been removed. It will trigger an event through formFieldRemoved
     * @param field
     */
    notifyFormFieldRemoved(field: string): void {
        this.formFieldRemoved.emit(field);
    }

    /**
     * Select all fields that are on the search filter
     */
    selectFieldsThatAreOnSearchFilter(): void {
        const filterNames: string[] = [];
        for (const property in this.searchModel.filter) {
            if (this.searchModel.filter[property] != undefined) {
                filterNames.push(property);
            }
        }

        let changed: boolean = false;
        const selectedFormFields: string[] = this.selectedFormFields;
        filterNames.forEach(filterName => {
            if (selectedFormFields.find(selectedFormField => selectedFormField == filterName) == undefined) {
                selectedFormFields.push(filterName);
                changed = true;
            }
        });
        if (changed) {
            this.selectedFormFields = selectedFormFields;
        }
        this.subscriptions.add(this.searchModel.clearFilterOptions$.subscribe(() => {
            this.selectedFormFields = [];
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
